import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getTableCellNodeFromLexicalNode, TableCellNode } from '@lexical/table'
import { $getSelection, $isRangeSelection } from 'lexical'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Popper, Box, Button, Theme, useMediaQuery } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { makeStyles } from 'tss-react/mui'

import { PopoverContent } from './PopoverContent'
import { MENU_POSITION_OFFSET_LEFT, MENU_POSITION_OFFSET_TOP } from './const'

const useStyles = makeStyles()(theme => ({
  button: {
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    svg: {
      height: 'auto',
    },
  },
  buttonIcon: {
    fontSize: 16,
    transition: '0.1s rotate',
  },
  popper: {
    zIndex: '9999',
  },
}))

interface MenuProps {
  anchorElem: HTMLElement
}

interface ContainerPosition {
  left: number
  top: number
}

export const Menu: FC<MenuProps> = ({ anchorElem }) => {
  const [editor] = useLexicalComposerContext()
  const { classes } = useStyles()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const menuRootRef = useRef(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuContainerPosition, setContainerPosition] = useState<ContainerPosition | null>(null)
  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(null)

  const moveMenu = useCallback(() => {
    const selection = $getSelection()
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (selection == null) {
      setTableMenuCellNode(null)
      return
    }

    const rootElement = editor.getRootElement()

    if ($isRangeSelection(selection) && rootElement !== null && nativeSelection !== null && rootElement.contains(nativeSelection.anchorNode)) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(selection.anchor.getNode())

      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null)
        return
      }

      const tableCellParentNodeDOM = editor.getElementByKey(tableCellNodeFromSelection.getKey())

      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null)
        return
      }

      setTableMenuCellNode(tableCellNodeFromSelection)
    } else if (!activeElement) {
      setTableMenuCellNode(null)
    }
  }, [editor])

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        moveMenu()
      })
    })
  })

  const tableCellNodeDOM = useMemo(() => {
    if (!tableCellNode) {
      return null
    }

    return editor.getElementByKey(tableCellNode.getKey())
  }, [tableCellNode, editor])

  useEffect(() => {
    setIsMenuOpen(false)

    if (!tableCellNodeDOM) {
      setContainerPosition(null)
      return
    }

    const tableCellRect = tableCellNodeDOM?.getBoundingClientRect()
    const anchorRect = anchorElem.getBoundingClientRect()

    if (!tableCellRect) {
      setContainerPosition(null)
      return
    }

    const top = tableCellRect.top - anchorRect.top + MENU_POSITION_OFFSET_TOP
    const left = tableCellRect.right - anchorRect.left - MENU_POSITION_OFFSET_LEFT

    setContainerPosition({
      left,
      top,
    })
  }, [tableCellNodeDOM, editor, anchorElem])

  if (!menuContainerPosition) {
    return null
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        left: menuContainerPosition.left,
        top: menuContainerPosition.top,
      }}
    >
      <Button
        type="button"
        className={classes.button}
        onClick={() => {
          setIsMenuOpen(isOpened => !isOpened)
        }}
        ref={menuRootRef}
        variant="contained"
      >
        <KeyboardArrowDownIcon className={classes.buttonIcon} sx={{ rotate: isMenuOpen ? '-90deg' : '' }} />
      </Button>
      <Popper
        open={isMenuOpen}
        anchorEl={tableCellNodeDOM}
        className={classes.popper}
        placement={isMobile ? 'bottom' : 'right-end'}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: isMobile ? [0, 0] : [0, 10],
              },
            },
          ],
        }}
        /** TODO check the values */
        // placeholder={undefined}
        // onPointerEnterCapture={undefined}
        // onPointerLeaveCapture={undefined}
      >
        <PopoverContent onClose={() => setIsMenuOpen(false)} tableCellNode={tableCellNode as TableCellNode} />
      </Popper>
    </Box>
  )
}
